// imports section Start
import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import currencyFormat from "../../../utils/common/CurrencyFormat";
import { CON_ORDER_EXCHANGE } from "../../../utils/sys/Constants";
// imports section End

/**
 * ReturnNote — reusable credit/exchange note component.
 *
 * Props:
 *   noteData   — { ordercode, date, items: [{MENUNAME, QTY, RATE, AMOUNT, REMARK}],
 *                  totalQty, totalAmount, notes, expiryDate }
 *   actionText — "Return" | "Exchange"
 *   storeName  — restaurant / company name
 *   address    — store address string
 *   isPrint    — boolean; when true uses print-optimised monospace styling,
 *                when false uses the page theme fonts/colours
 */
function ReturnNote({ noteData, actionText, storeName, address, isPrint = false }) {
  const theme = useTheme();

  const isExchange = actionText === CON_ORDER_EXCHANGE;
  const noteTitle = isExchange ? "Exchange Note" : "Credit Note";
  const footerMsg = isExchange
    ? "Present this note to exchange your item(s)."
    : "Present this note to claim your refund.";

  // ── Style helpers ──────────────────────────────────────────────────────────
  // Print mode: monospace, black-on-white, fixed widths for POS printer
  // Page mode:  theme fonts, theme colours, readable on screen
  const fontFamily = isPrint ? "'Courier New', monospace" : "inherit";
  const color = isPrint ? "#000" : theme.palette.text.primary;
  const secondaryColor = isPrint ? "#444" : theme.palette.text.secondary;
  const mutedColor = isPrint ? "#555" : theme.palette.text.secondary;
  const borderStyle = isPrint
    ? "1px dashed #000"
    : `1px dashed ${theme.palette.stroke?.main || "#D7D7D7"}`;
  const solidBorder = isPrint
    ? "1px solid #000"
    : `1px solid ${theme.palette.stroke?.main || "#D7D7D7"}`;

  const labelSx = {
    fontFamily,
    fontSize: isPrint ? "10px" : "0.75rem",
    color: secondaryColor,
    lineHeight: 1.4,
  };
  const valueSx = {
    fontFamily,
    fontSize: isPrint ? "10px" : "0.75rem",
    fontWeight: 600,
    color,
    lineHeight: 1.4,
  };
  const titleSx = {
    fontFamily,
    fontSize: isPrint ? "12px" : "0.875rem",
    fontWeight: 700,
    color,
    letterSpacing: isPrint ? "0.05em" : "0.02em",
  };
  const storeSx = {
    fontFamily,
    fontSize: isPrint ? "13px" : "1rem",
    fontWeight: 700,
    color,
    lineHeight: 1.3,
    textAlign: "center",
  };
  const addressSx = {
    fontFamily,
    fontSize: isPrint ? "10px" : "0.7rem",
    color: mutedColor,
    lineHeight: 1.3,
    textAlign: "center",
  };
  const itemNameSx = {
    fontFamily,
    fontSize: isPrint ? "10px" : "0.75rem",
    color,
    flex: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: isPrint ? 120 : 160,
  };
  const colSx = (width, align = "right") => ({
    fontFamily,
    fontSize: isPrint ? "10px" : "0.75rem",
    color,
    width,
    textAlign: align,
    flexShrink: 0,
  });
  const totalLabelSx = {
    fontFamily,
    fontSize: isPrint ? "12px" : "0.875rem",
    fontWeight: 700,
    color,
  };
  const footerSx = {
    fontFamily,
    fontSize: isPrint ? "9px" : "0.7rem",
    color: mutedColor,
    textAlign: "center",
    lineHeight: 1.5,
  };

  return (
    <Box
      sx={{
        width: isPrint ? 280 : "100%",
        color,
        fontFamily,
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ textAlign: "center", mb: 1 }}>
        {storeName && <Typography sx={storeSx}>{storeName}</Typography>}
        {address && (
          <Typography sx={addressSx}>{address}</Typography>
        )}
        <Box
          sx={{
            borderTop: borderStyle,
            borderBottom: borderStyle,
            my: 0.75,
            py: 0.5,
          }}
        >
          <Typography sx={titleSx}>
            {isPrint ? `*** ${noteTitle.toUpperCase()} ***` : noteTitle}
          </Typography>
        </Box>
      </Box>

      {/* ── Meta ── */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography sx={labelSx}>Order #</Typography>
          <Typography sx={valueSx}>{noteData?.invno || noteData?.ordercode}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography sx={labelSx}>CN/EX No</Typography>
          <Typography sx={valueSx}>{noteData?.returnno || "—"}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography sx={labelSx}>Date</Typography>
          <Typography sx={{ ...labelSx, color }}>{noteData?.date}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography sx={labelSx}>Expiry Date</Typography>
          <Typography sx={valueSx}>{noteData?.expiryDate || "—"}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={labelSx}>Type</Typography>
          <Typography sx={valueSx}>{actionText}</Typography>
        </Box>
      </Box>

      {/* ── Column headers ── */}
      <Box
        sx={{
          borderTop: borderStyle,
          borderBottom: borderStyle,
          py: 0.4,
          display: "flex",
          mb: 0.5,
        }}
      >
        <Typography sx={{ ...itemNameSx, fontWeight: 700 }}>Item</Typography>
        <Typography sx={{ ...colSx(28, "center"), fontWeight: 700 }}>Qty</Typography>
        <Typography sx={{ ...colSx(56, "right"), fontWeight: 700 }}>Rate</Typography>
        <Typography sx={{ ...colSx(60, "right"), fontWeight: 700 }}>Amount</Typography>
      </Box>

      {/* ── Items ── */}
      {(noteData?.items || []).map((item, i) => (
        <Box key={i} sx={{ mb: 0.25 }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={itemNameSx}>{item.MENUNAME}</Typography>
            <Typography sx={colSx(28, "center")}>{item.QTY}</Typography>
            <Typography sx={colSx(56, "right")}>{currencyFormat(item.RATE)}</Typography>
            <Typography sx={colSx(60, "right")}>{currencyFormat(item.AMOUNT)}</Typography>
          </Box>
          {item.REMARK && (
            <Typography
              sx={{
                fontFamily,
                fontSize: isPrint ? "9px" : "0.68rem",
                color: mutedColor,
                pl: 1,
              }}
            >
              ↳ {item.REMARK}
            </Typography>
          )}
        </Box>
      ))}

      {/* ── Totals ── */}
      <Box sx={{ borderTop: borderStyle, mt: 0.75, pt: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography sx={labelSx}>Total Qty</Typography>
          <Typography sx={valueSx}>{noteData?.totalQty}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={totalLabelSx}>Total Amount</Typography>
          <Typography sx={{ ...totalLabelSx, color: isPrint ? "#000" : theme.palette.primary?.main }}>
            {currencyFormat(noteData?.totalAmount || 0)}
          </Typography>
        </Box>
      </Box>

      {/* ── Notes ── */}
      {noteData?.notes && (
        <Box sx={{ borderTop: borderStyle, mt: 0.75, pt: 0.5 }}>
          <Typography sx={{ ...labelSx, fontStyle: "italic" }}>
            Note: {noteData.notes}
          </Typography>
        </Box>
      )}

      {/* ── Footer ── */}
      <Box sx={{ borderTop: borderStyle, mt: 1, pt: 0.5 }}>
        <Typography sx={footerSx}>{footerMsg}</Typography>
        <Typography sx={{ ...footerSx, mt: 0.25 }}>
          Thank you for your patience.
        </Typography>
      </Box>
    </Box>
  );
}

export default ReturnNote;
